import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { FeatherTrophy, FeatherGift } from '@subframe/core';
import { Button } from '../../ui/components/Button';
import { Badge } from '../../ui/components/Badge';
import { ResultsModal } from './ResultsModal';
import { WalletAddress } from './WalletAddress';
import type { ContestView, SweepstakesWinner } from '../../api/sweepstakes';

interface ResultsBannerProps {
  contestView: ContestView;
}

export const ResultsBanner: React.FC<ResultsBannerProps> = ({ contestView }) => {
  const [showModal, setShowModal] = useState(false);
  const { contest, stats, winners } = contestView;

  // Show for ended contests (awaiting draw) and resolved contests within 7 days of draw
  if (contest.status === 'ended') {
    // Always show while winners haven't been drawn
  } else if (contest.status === 'resolved' && contest.resolved_at) {
    const resolvedAt = new Date(contest.resolved_at).getTime();
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    if (resolvedAt < sevenDaysAgo) return null;
  } else {
    return null;
  }

  // Group winners by prize tier, keeping one representative per tier
  const winnersByTier: { tierName: string; tierType: string; winner: SweepstakesWinner }[] = [];
  if (winners?.length) {
    const seen = new Set<string>();
    for (const w of winners) {
      if (!seen.has(w.prize_tier_id)) {
        seen.add(w.prize_tier_id);
        winnersByTier.push({ tierName: w.tier_name, tierType: w.tier_type, winner: w });
      }
    }
  }

  return (
    <>
      <div className="w-full border border-neutral-border rounded-md px-4 py-3 mb-4 bg-default-background">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="neutral">CONTEST ENDED</Badge>
              <span className="font-medium text-default-font truncate">{contest.name}</span>
              <span className="text-sm text-subtext-color shrink-0">
                Ended {formatDistanceToNow(new Date(contest.ends_at))} ago
              </span>
            </div>
            <div className="flex flex-col text-sm text-subtext-color mt-1">
              <span>{stats.entries} total entries from {stats.participants} participants</span>
              {winnersByTier.map(({ tierName, tierType, winner }) => (
                <span key={winner.prize_tier_id} className="inline-flex items-center gap-1">
                  {tierType === 'leaderboard'
                    ? <FeatherTrophy className="h-3.5 w-3.5 text-yellow-500" />
                    : <FeatherGift className="h-3.5 w-3.5 text-brand-500" />
                  }
                  {tierName} winner:{' '}
                  <WalletAddress address={winner.wallet_address} />
                </span>
              ))}
              {!winners?.length && (
                <span className="text-yellow-400">Winners are being drawn...</span>
              )}
              {contest.proof_links && contest.proof_links.length > 0 && (
                <span className="text-success-600 text-xs">Prize distribution verified ({contest.proof_links.length} transaction{contest.proof_links.length > 1 ? 's' : ''})</span>
              )}
            </div>
          </div>
          <Button variant="neutral-secondary" onClick={() => setShowModal(true)}>
            View Full Results
          </Button>
        </div>
      </div>

      <ResultsModal
        contestView={contestView}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};
